import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { format, lastDayOfMonth, eachWeekOfInterval, eachDayOfInterval, endOfWeek, getISOWeek, isSameMonth, addDays, isPast, isSameDay } from 'date-fns';
import MonthRow from './MonthRow.js';
import MonthHeader from './MonthHeader.js';
import { monthNames } from './texts.js';
import { MonthWrapper, MonthTitle, Month, MonthFooter } from './style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useIsMobile from '../../hooks/useIsMobile.js';
import useGlobals from '../../hooks/useGlobals.js';

/* eslint-disable react/display-name */
/* eslint-disable no-param-reassign */

var getMonthData = function getMonthData(inputDate, validDates, siteId) {
  var lastDate = lastDayOfMonth(inputDate);
  var weeks = eachWeekOfInterval({
    start: inputDate,
    end: lastDate
  }, {
    weekStartsOn: 1
  });
  var now = new Date(Date.now());
  return weeks.map(function (firstDayInWeek) {
    var days = eachDayOfInterval({
      start: firstDayInWeek,
      end: endOfWeek(firstDayInWeek, {
        weekStartsOn: 1
      })
    });
    return {
      weekNr: getISOWeek(firstDayInWeek),
      days: days.map(function (day) {
        if (isSameMonth(inputDate, day)) {
          var dateString = format(day, 'yyyy-MM-dd');
          var disabled = !!validDates.length && validDates.indexOf(dateString) === -1;
          if (siteId === 18) {
            // Disable all days up to 3 days ahead from now (Globetrotter exclusive). Current day + 3 days = 4 days total
            var threeDaysAhead = addDays(now, 3);
            if (day < threeDaysAhead) {
              disabled = true;
            }
          } else if (isPast(day) && !isSameDay(day, now)) {
            disabled = true;
          }
          return {
            date: day.getDate(),
            disabled: disabled,
            fullDate: dateString
          };
        }
        return null;
      })
    };
  });
};
var MonthBlock = /*#__PURE__*/React.memo(function (_ref) {
  var month = _ref.month,
    year = _ref.year,
    validDates = _ref.validDates,
    recommendedDates = _ref.recommendedDates,
    selectedDate = _ref.selectedDate,
    selectedDateInterval = _ref.selectedDateInterval,
    onChange = _ref.onChange,
    monthFooter = _ref.monthFooter,
    useDynamicRangeSelect = _ref.useDynamicRangeSelect,
    dateHovered = _ref.dateHovered,
    setDateHovered = _ref.setDateHovered,
    dynamicRangeInProgress = _ref.dynamicRangeInProgress,
    offers = _ref.offers,
    datePickerHasOffers = _ref.datePickerHasOffers,
    datePickerHasSelectedMonths = _ref.datePickerHasSelectedMonths,
    monthIsSelected = _ref.monthIsSelected,
    nrOfMonthsDisplayedDesktop = _ref.nrOfMonthsDisplayedDesktop,
    navigateToNextMonth = _ref.navigateToNextMonth,
    navigateToPrevMonth = _ref.navigateToPrevMonth,
    isMonthBlockVisible = _ref.isMonthBlockVisible,
    datePickerBodyPosition = _ref.datePickerBodyPosition;
  var isMobile = useIsMobile();
  var monthWrapperRef = useRef(null);
  var _useGlobals = useGlobals(),
    culture = _useGlobals.culture,
    siteId = _useGlobals.siteId;
  useEffect(function () {
    if (monthWrapperRef.current) {
      var focusableElements = monthWrapperRef.current.querySelectorAll('button, [href], input, select, textarea');
      for (var i = 0; i < focusableElements.length; i += 1) {
        focusableElements[i].tabIndex = isMobile || isMonthBlockVisible ? 0 : -1;
      }
    }
  }, [isMobile, isMonthBlockVisible]);
  if (month > 11 || month < 0) {
    throw new TypeError('Month must be between 0-11');
  }
  var inputDate = useMemo(function () {
    var res = new Date();
    res.setFullYear(year, month, 1);
    return res;
  }, [year, month]);
  var monthData = useMemo(function () {
    return getMonthData(inputDate, validDates, siteId);
  }, [inputDate, siteId, validDates]);
  var monthName = monthNames[culture][month];
  var monthDateString = format(new Date(year, month), 'yyyy-MM');
  var isElementInDatePickerBody = useCallback(function (el) {
    var rect = el.getBoundingClientRect();
    var left = datePickerBodyPosition.left,
      right = datePickerBodyPosition.right;
    return rect.left > left && rect.right < right;
  }, [datePickerBodyPosition]);
  var focusNextDate = function focusNextDate(daysToAddOrSubtract) {
    var currentFocusedDate = document.activeElement.getAttribute('data-date');
    if (!currentFocusedDate) return;
    var nextDateToFocus = addDays(new Date(currentFocusedDate), daysToAddOrSubtract);
    var nextDateToFocusFormatted = format(nextDateToFocus, 'yyyy-MM-dd');
    var nextDateToFocusBtnElement = document.querySelector("button[data-date=\"".concat(nextDateToFocusFormatted, "\"]"));
    if (!nextDateToFocusBtnElement) return;
    var isVisibleInDatePickerBody = isElementInDatePickerBody(nextDateToFocusBtnElement);
    if (!isVisibleInDatePickerBody) {
      if (daysToAddOrSubtract > 0) navigateToNextMonth();else navigateToPrevMonth();
      nextDateToFocusBtnElement.focus({
        preventScroll: true
      });
      return;
    }
    nextDateToFocusBtnElement.focus();
  };
  var handleKeyDown = function handleKeyDown(event) {
    var keyActions = {
      ArrowUp: -7,
      ArrowDown: 7,
      ArrowLeft: -1,
      ArrowRight: 1
    };
    if (keyActions[event.key] !== undefined) {
      event.preventDefault();
      focusNextDate(keyActions[event.key]);
    }
  };
  return /*#__PURE__*/jsxs(MonthWrapper, {
    $nrOfMonthsDisplayedDesktop: nrOfMonthsDisplayedDesktop,
    ref: monthWrapperRef,
    children: [/*#__PURE__*/jsx(MonthTitle, {
      children: "".concat(monthName, " ").concat(year)
    }), /*#__PURE__*/jsxs(Month, {
      onKeyDown: handleKeyDown,
      children: [/*#__PURE__*/jsx(MonthHeader, {}), monthData.map(function (week) {
        return /*#__PURE__*/jsx(MonthRow, {
          dateHovered: dateHovered,
          datePickerHasOffers: datePickerHasOffers,
          datePickerHasSelectedMonths: datePickerHasSelectedMonths,
          dynamicRangeInProgress: dynamicRangeInProgress,
          month: month,
          monthIsSelected: monthIsSelected,
          offers: offers,
          onSelectDay: onChange,
          recommendedDates: recommendedDates,
          selectedDateInterval: selectedDateInterval,
          selectedDay: selectedDate,
          setDateHovered: setDateHovered,
          siteId: siteId,
          useDynamicRangeSelect: useDynamicRangeSelect,
          weekDays: week.days,
          weekNumber: week.weekNr
        }, "week-".concat(week.weekNr));
      })]
    }), /*#__PURE__*/jsx(MonthFooter, {
      children: monthFooter({
        month: month,
        monthDateString: monthDateString,
        monthName: monthName,
        year: year
      })
    })]
  });
});
MonthBlock.defaultProps = {
  validDates: [],
  recommendedDates: [],
  selectedDate: '',
  selectedDateInterval: {},
  onChange: function onChange() {},
  monthFooter: function monthFooter() {},
  useDynamicRangeSelect: false,
  dateHovered: null,
  setDateHovered: function setDateHovered() {},
  dynamicRangeInProgress: false,
  offers: [],
  datePickerHasOffers: false,
  monthIsSelected: false,
  datePickerHasSelectedMonths: false,
  datePickerBodyPosition: {
    left: undefined,
    right: undefined
  }
};
var MonthBlock$1 = MonthBlock;

export { MonthBlock$1 as default };
//# sourceMappingURL=MonthBlock.js.map
